import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { cn } from '../../../../../common/src/utils/cn';
import ArrivalsMap from './ArrivalsMap';
import type { ArrivalMap, MapFilter, MapTone } from './map-model';
import { unitLabel, type Arrival } from './model';

const MONTHS = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sept',
  'oct',
  'nov',
  'dic',
];
const TONE_DOT: Record<MapTone, string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
};
const TONE_TEXT: Record<MapTone, string> = {
  danger: 'text-danger',
  warning: 'text-warning-600',
  success: 'text-success-600',
};

const dateFor = (offsetDays: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]}`;
};
const relative = (offsetDays: number) =>
  offsetDays === 0
    ? 'Hoy'
    : offsetDays === 1
      ? 'Mañana'
      : `en ${offsetDays} días`;
const formatKg = (kg: number) => `${Math.round(kg).toLocaleString('es-CL')} kg`;

const Legend = ({ live, estimated }: { live: number; estimated: number }) => (
  <div className='pointer-events-none absolute bottom-2 left-2 right-2 flex flex-wrap gap-x-3 gap-y-0.5 rounded-lg bg-content1/90 px-2 py-1 text-[11px] text-default-600 shadow-small sm:bottom-3 sm:left-3 sm:right-auto sm:gap-x-4 sm:px-3 sm:py-1.5'>
    <span className='flex items-center gap-1.5'>
      <span className='h-2 w-2 rounded-full bg-danger' />
      Arriba hoy o mañana
    </span>
    <span className='flex items-center gap-1.5'>
      <span className='h-2 w-2 rounded-full bg-warning' />
      Con retraso
    </span>
    <span className='flex items-center gap-1.5'>
      <span className='h-2 w-2 rounded-full bg-success' />
      En plazo
    </span>
    {live > 0 && (
      <span className='flex items-center gap-1.5'>
        <span className='h-2 w-2 rounded-full bg-default-600' />
        Posición real ({live})
      </span>
    )}
    {estimated > 0 && (
      <span className='flex items-center gap-1.5'>
        <span className='h-2 w-2 rounded-full border border-default-600' />
        Posición estimada ({estimated})
      </span>
    )}
  </div>
);

const DestinationRow = ({
  destination,
  selected,
  onSelect,
}: {
  destination: ArrivalMap['destinations'][number];
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type='button'
    onClick={onSelect}
    aria-pressed={selected}
    className={cn(
      'flex w-full items-center gap-3 border-b border-default-100 px-4 py-3 text-left transition-colors hover:bg-default-100',
      selected &&
        'bg-primary/10 shadow-[inset_3px_0_0_hsl(var(--nextui-primary))]'
    )}
  >
    <span
      className={cn(
        'h-2.5 w-2.5 flex-none rounded-full',
        TONE_DOT[destination.tone]
      )}
    />
    <span className='min-w-0 flex-1'>
      <span className='flex items-center gap-2'>
        <span className='truncate text-sm font-semibold'>
          {destination.flag} {destination.name}
        </span>
        {/^[A-Z0-9]{3,5}$/.test(destination.pod) && (
          <span className='font-mono text-[10px] font-semibold text-default-400'>
            {destination.pod}
          </span>
        )}
      </span>
      <span className='mt-0.5 block text-xs text-default-500'>
        {unitLabel(destination.arrivals)} ·{' '}
        {(destination.kg / 1000).toLocaleString('es-CL', {
          maximumFractionDigits: 1,
        })}{' '}
        t{!destination.coordinates && ' · sin ubicación'}
      </span>
    </span>
    {Number.isFinite(destination.minOffset) && (
      <span className='text-right'>
        <span className='block text-sm font-semibold'>
          {dateFor(destination.minOffset)}
        </span>
        <span
          className={cn(
            'mt-0.5 block text-[11px]',
            TONE_TEXT[destination.tone]
          )}
        >
          {relative(destination.minOffset)}
        </span>
      </span>
    )}
  </button>
);

const ArrivalLine = ({
  arrival,
  onTracking,
}: {
  arrival: Arrival;
  onTracking: (arrival: Arrival) => void;
}) => (
  <div className='border-b border-default-100 px-4 py-3 hover:bg-default-50'>
    <div className='flex items-center gap-2'>
      <span className='truncate font-mono text-[13px] font-semibold'>
        {arrival.container}
      </span>
      <Chip
        size='sm'
        variant='flat'
        color={
          arrival.arrived
            ? 'success'
            : ['Nave zarpó', 'Vuelo despegó'].includes(arrival.milestone)
              ? 'primary'
              : 'default'
        }
      >
        {arrival.milestone}
      </Chip>
      <span className='ml-auto whitespace-nowrap text-sm font-bold'>
        {arrival.offsetDays === null ? 'Sin ETA' : dateFor(arrival.offsetDays)}
      </span>
    </div>
    <div className='mt-1.5 flex items-center gap-2 text-xs text-default-500'>
      <span className='truncate font-semibold text-default-700'>
        {arrival.client}
      </span>
      <span className='whitespace-nowrap'>
        {arrival.flag} {arrival.portName}
      </span>
      <span className='ml-auto whitespace-nowrap'>{formatKg(arrival.kg)}</span>
    </div>
    <div className='mt-1 flex items-center gap-2'>
      <span className='truncate text-[11px] text-default-400'>
        {arrival.mode === 'air'
          ? `Aéreo · ${arrival.vessel}`
          : `${arrival.vessel} · Viaje ${arrival.voyage}`}
      </span>
      {(arrival.delayDays ?? 0) > 0 && (
        <Chip size='sm' variant='flat' color='danger'>
          +{arrival.delayDays}d vs. plan
        </Chip>
      )}
      <Button
        size='sm'
        variant='light'
        isIconOnly
        className='ml-auto'
        aria-label={`Ver seguimiento de ${arrival.container}`}
        onPress={() => onTracking(arrival)}
      >
        <Icon icon='tabler:route' width={16} />
      </Button>
    </div>
  </div>
);

export default function ArrivalsMapPanel({
  map,
  arrivals,
  filtered,
  horizon,
  filter,
  onFilter,
  onTracking,
}: {
  map: ArrivalMap;
  /** Arrivals in the window, before the map filter. */
  arrivals: Arrival[];
  /** Arrivals picked on the map (all of them without a filter). */
  filtered: Arrival[];
  horizon: number;
  filter: MapFilter | null;
  onFilter: (filter: MapFilter | null) => void;
  onTracking: (arrival: Arrival) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const togglePod = (pod: string, label: string) =>
    onFilter(
      filter?.kind === 'pod' && filter.key === pod
        ? null
        : { kind: 'pod', key: pod, label }
    );
  const days = `${horizon} ${horizon === 1 ? 'día' : 'días'}`;
  // A picked route or origin lists its shipments; otherwise, the destinations.
  const showShipments = filter && filter.kind !== 'pod';
  const openTracking = (arrival: Arrival) => {
    setExpanded(false);
    onTracking(arrival);
  };

  return (
    <>
      <Card shadow='sm' className='mt-4'>
        <CardBody className='flex flex-col p-0 lg:flex-row'>
          <div className='relative min-w-0 flex-1'>
            <ArrivalsMap
              map={map}
              filter={filter}
              onFilter={onFilter}
              backgroundAction={{
                label: 'Clic para ampliar',
                icon: 'tabler:arrows-maximize',
                onPress: () => setExpanded(true),
              }}
              height={420}
            />
            <Button
              size='sm'
              variant='flat'
              className='absolute right-3 top-3 bg-content1/90'
              startContent={<Icon icon='tabler:arrows-maximize' width={15} />}
              onPress={() => setExpanded(true)}
            >
              Ampliar
            </Button>
            <Legend live={map.live} estimated={map.estimated} />
          </div>
          <div className='flex max-h-[420px] flex-col border-t border-default-200 lg:w-[340px] lg:border-l lg:border-t-0'>
            {showShipments ? (
              <>
                <div className='flex items-center gap-2 border-b border-default-200 px-4 py-3'>
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                      {filter.kind === 'route'
                        ? 'Envíos en esta ruta'
                        : 'Envíos desde este origen'}
                    </p>
                    <p className='mt-0.5 line-clamp-2 text-sm font-semibold'>
                      {filter.label}
                    </p>
                    <p className='text-xs text-default-400'>
                      {unitLabel(filtered)}
                    </p>
                  </div>
                  <Button
                    size='sm'
                    variant='light'
                    className='shrink-0'
                    onPress={() => onFilter(null)}
                  >
                    Quitar filtro
                  </Button>
                </div>
                <div className='overflow-y-auto'>
                  {filtered.map((arrival) => (
                    <ArrivalLine
                      key={arrival.id}
                      arrival={arrival}
                      onTracking={openTracking}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className='flex items-baseline justify-between gap-2 border-b border-default-200 px-4 py-3'>
                  <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                    Destinos en ventana
                  </span>
                  <span className='text-xs text-default-400'>
                    Toca para filtrar
                  </span>
                </div>
                <div className='overflow-y-auto'>
                  {map.destinations.map((destination) => (
                    <DestinationRow
                      key={destination.pod}
                      destination={destination}
                      selected={
                        filter?.kind === 'pod' && filter.key === destination.pod
                      }
                      onSelect={() =>
                        togglePod(destination.pod, destination.name)
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Modal
        size='full'
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        hideCloseButton
        scrollBehavior='inside'
      >
        <ModalContent>
          {(close) => (
            <ModalBody className='flex min-h-0 flex-col gap-0 p-0'>
              <div className='flex flex-wrap items-center gap-3 border-b border-default-200 px-4 py-3 sm:px-5'>
                <Button
                  size='sm'
                  variant='flat'
                  startContent={
                    <Icon icon='tabler:arrows-minimize' width={15} />
                  }
                  onPress={close}
                >
                  Reducir
                </Button>
                <span className='text-base font-bold'>
                  Gestor de Arribos · Mapa
                </span>
                <span className='text-sm font-semibold text-primary'>
                  Próximos {days}
                </span>
                <span className='text-sm text-default-500'>
                  {unitLabel(arrivals)}
                </span>
                <div className='flex w-full flex-wrap gap-2 lg:ml-auto lg:w-auto'>
                  {map.destinations.map((destination) => {
                    const active =
                      filter?.kind === 'pod' && filter.key === destination.pod;
                    return (
                      <Chip
                        key={destination.pod}
                        as='button'
                        variant={active ? 'solid' : 'bordered'}
                        color={active ? 'primary' : 'default'}
                        startContent={
                          <span
                            className={cn(
                              'ml-1 h-2 w-2 rounded-full',
                              TONE_DOT[destination.tone]
                            )}
                          />
                        }
                        onClick={() =>
                          togglePod(destination.pod, destination.name)
                        }
                      >
                        <span className='font-semibold'>
                          {destination.name}
                        </span>{' '}
                        <span className='opacity-70'>
                          {
                            new Set(
                              destination.arrivals.map((a) => a.containerKey)
                            ).size
                          }
                        </span>
                      </Chip>
                    );
                  })}
                </div>
              </div>
              <div className='flex min-h-0 flex-1 flex-col lg:flex-row'>
                <div className='relative h-[45vh] min-w-0 lg:h-auto lg:flex-1'>
                  <ArrivalsMap
                    map={map}
                    filter={filter}
                    onFilter={onFilter}
                    backgroundAction={
                      filter
                        ? {
                            label: 'Clic para quitar el filtro',
                            icon: 'tabler:filter-off',
                            onPress: () => onFilter(null),
                          }
                        : undefined
                    }
                    fill
                  />
                  <Legend live={map.live} estimated={map.estimated} />
                </div>
                <div className='flex min-h-0 flex-1 flex-col border-t border-default-200 lg:w-[min(480px,40vw)] lg:flex-none lg:border-l lg:border-t-0'>
                  <div className='flex items-center gap-3 border-b border-default-200 px-4 py-3'>
                    <div className='min-w-0'>
                      <p className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                        {filter ? filter.label : 'Arribos'}
                      </p>
                      <p className='text-xs text-default-400'>
                        {unitLabel(filtered)} · por ETA
                      </p>
                    </div>
                    {filter && (
                      <Button
                        size='sm'
                        variant='light'
                        className='ml-auto shrink-0'
                        onPress={() => onFilter(null)}
                      >
                        Quitar filtro
                      </Button>
                    )}
                  </div>
                  <div className='min-h-0 flex-1 overflow-y-auto'>
                    {filtered.map((arrival) => (
                      <ArrivalLine
                        key={arrival.id}
                        arrival={arrival}
                        onTracking={openTracking}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </ModalBody>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
